import { toast } from "sonner";
import { useGetComgateCommentsQuery } from "Api/Queries/Merchants/useGetComgateCommentsQuery";
import { useSendComgateCommentMutation } from "Api/Mutations/Merchants/useSendComgateCommentMutation";
import { useResubmitOrbitLToComgateMutation } from "Api/Mutations/Merchants/useResubmitOrbitLToComgateMutation";
import { Resources, useResource } from "Translations/Resources";
import { isComgateStatusRequiringAction } from "Components/Merchants/OrbitLOnboarding/utils";
import type { ComgateOnboardingStatus } from "Infrastructure/Api/Api";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding.Detail;

type Params = {
  merchantPublicID: string;
  comgateMID: string | null | undefined;
  comgateStatus: ComgateOnboardingStatus | null | undefined;
  onCommentSent?: () => void;
  onResubmitted?: () => void;
};

export const useOrbitLOnboardingDetailActions = ({
  merchantPublicID,
  comgateMID,
  comgateStatus,
  onCommentSent,
  onResubmitted,
}: Params) => {
  const { t } = useResource();

  const { data: comments, isLoading: isCommentsLoading } =
    useGetComgateCommentsQuery(comgateMID ? merchantPublicID : undefined);

  const { mutate: sendCommentMutate, isPending: isSendingComment } =
    useSendComgateCommentMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SendNoteSuccess));
        onCommentSent?.();
      },
      onError: () => toast.error(t(RESOURCES.SendNoteError)),
    });

  const { mutate: resubmitMutate, isPending: isResubmitting } =
    useResubmitOrbitLToComgateMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.ResubmitSuccess));
        onResubmitted?.();
      },
      onError: () => toast.error(t(RESOURCES.ResubmitError)),
    });

  const sendComment = (commentText: string) => {
    const trimmed = commentText.trim();

    if (!trimmed) {
      return;
    }

    sendCommentMutate({ merchantPublicID, commentText: trimmed });
  };

  const resubmit = () => {
    resubmitMutate({ merchantPublicID });
  };

  const canResubmit = isComgateStatusRequiringAction(comgateStatus);

  return {
    comments,
    isCommentsLoading,
    sendComment,
    isSendingComment,
    resubmit,
    isResubmitting,
    canResubmit,
  };
};
